import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3, TrendingUp, Eye, Clock, Globe, 
  Monitor, Smartphone, Tablet, Image, FileText, ArrowUpRight,
  Calendar, Star, MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import { adminFetch } from '../../utils/adminFetch';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple bar chart component
const SimpleBarChart = ({ data, labelKey, valueKey, maxValue, color = 'bg-primary' }) => {
  const max = maxValue || Math.max(...data.map(d => d[valueKey]), 1);
  
  return (
    <div className="space-y-2">
      {data.map((item, index) => (
        <div key={index} className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground w-20 truncate">
            {item[labelKey]}
          </span>
          <div className="flex-1 h-4 bg-muted rounded overflow-hidden">
            <div 
              className={`h-full ${color} transition-all duration-500`}
              style={{ width: `${(item[valueKey] / max) * 100}%` }}
            />
          </div>
          <span className="text-xs font-medium w-12 text-right">{item[valueKey]}</span>
        </div>
      ))}
    </div>
  );
};

// Line chart for daily views
const DailyChart = ({ data }) => {
  if (!data || data.length === 0) return <p className="text-sm text-muted-foreground">Sin datos</p>;
  
  const maxViews = Math.max(...data.map(d => d.views), 1);
  const chartHeight = 120;
  
  return (
    <div className="relative" style={{ height: chartHeight + 30 }}>
      <div className="absolute inset-0 flex items-end justify-between gap-1 pb-6">
        {data.slice(-14).map((day, index) => {
          const height = (day.views / maxViews) * chartHeight;
          return (
            <div key={index} className="flex-1 flex flex-col items-center gap-1">
              <div 
                className="w-full bg-primary/80 hover:bg-primary transition-colors rounded-t cursor-pointer relative group"
                style={{ height: Math.max(height, 4) }}
              >
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-foreground text-background text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  {day.views} visitas
                </div>
              </div>
              <span className="text-[10px] text-muted-foreground">
                {new Date(day.date).getDate()}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Hour distribution chart
const HourlyChart = ({ data }) => {
  if (!data || data.length === 0) return <p className="text-sm text-muted-foreground">Sin datos</p>;
  
  // Fill in missing hours
  const hourData = Array.from({ length: 24 }, (_, i) => {
    const found = data.find(d => d.hour === i);
    return { hour: i, views: found ? found.views : 0 };
  });
  
  const maxViews = Math.max(...hourData.map(d => d.views), 1);
  
  return (
    <div className="flex items-end gap-[2px] h-20">
      {hourData.map((hour) => {
        const height = (hour.views / maxViews) * 100;
        return (
          <div key={hour.hour} className="flex-1 flex flex-col items-center gap-1 group relative">
            <div 
              className="w-full bg-blue-500/70 hover:bg-blue-500 transition-colors rounded-t cursor-pointer"
              style={{ height: `${Math.max(height, 4)}%` }}
            />
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-foreground text-background text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
              {hour.hour}:00 - {hour.views} visitas
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Device distribution pie-like display
const DeviceStats = ({ devices }) => {
  if (!devices || devices.length === 0) return <p className="text-sm text-muted-foreground">Sin datos</p>;
  
  const total = devices.reduce((sum, d) => sum + d.count, 0);
  const icons = {
    desktop: Monitor,
    mobile: Smartphone,
    tablet: Tablet
  };
  const colors = {
    desktop: 'bg-blue-500',
    mobile: 'bg-green-500',
    tablet: 'bg-purple-500'
  };
  
  return (
    <div className="space-y-3">
      {devices.map((device) => {
        const Icon = icons[device.device] || Monitor;
        const percentage = ((device.count / total) * 100).toFixed(1);
        return (
          <div key={device.device} className="flex items-center gap-3">
            <Icon className="w-5 h-5 text-muted-foreground" />
            <div className="flex-1">
              <div className="flex justify-between text-sm mb-1">
                <span className="capitalize">{device.device}</span>
                <span className="text-muted-foreground">{percentage}%</span>
              </div>
              <div className="h-2 bg-muted rounded overflow-hidden">
                <div 
                  className={`h-full ${colors[device.device] || 'bg-gray-500'}`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = 'text-primary' }) => (
  <div className="bg-card border border-border p-4">
    <div className="flex items-start justify-between mb-2">
      <Icon className={`w-5 h-5 ${color}`} />
      {trend && (
        <span className={`text-xs flex items-center gap-1 ${trend > 0 ? 'text-green-500' : 'text-red-500'}`}>
          <ArrowUpRight className={`w-3 h-3 ${trend < 0 ? 'rotate-180' : ''}`} />
          {Math.abs(trend)}%
        </span>
      )}
    </div>
    <p className="text-2xl font-bold">{value.toLocaleString()}</p>
    <p className="text-xs text-muted-foreground">{title}</p>
    {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
  </div>
);

export const AdminAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [viewsByDay, setViewsByDay] = useState([]);
  const [viewsByHour, setViewsByHour] = useState([]);
  const [topPages, setTopPages] = useState([]);
  const [topGalleries, setTopGalleries] = useState({ most_visited: [], best_rated: [] });
  const [topPosts, setTopPosts] = useState({ most_visited: [], best_rated: [] });
  const [referrers, setReferrers] = useState([]);
  const [deviceStats, setDeviceStats] = useState({ devices: [], browsers: [] });
  const [visitorStats, setVisitorStats] = useState({ by_country: [], by_city: [], total_30d: 0, total_7d: 0, total_24h: 0 });

  useEffect(() => {
    fetchAllAnalytics();
  }, []);

  const fetchAllAnalytics = async () => {
    setLoading(true);
    try {
      const endpoints = [
        { url: `${API}/admin/analytics/overview`, setter: setOverview },
        { url: `${API}/admin/analytics/views-by-day`, setter: setViewsByDay },
        { url: `${API}/admin/analytics/views-by-hour`, setter: setViewsByHour },
        { url: `${API}/admin/analytics/top-pages`, setter: setTopPages },
        { url: `${API}/admin/analytics/top-galleries`, setter: setTopGalleries },
        { url: `${API}/admin/analytics/top-posts`, setter: setTopPosts },
        { url: `${API}/admin/analytics/referrers`, setter: setReferrers },
        { url: `${API}/admin/analytics/devices`, setter: setDeviceStats },
        { url: `${API}/admin/visitor-stats`, setter: setVisitorStats }
      ];

      await Promise.all(endpoints.map(async ({ url, setter }) => {
        try {
          const response = await adminFetch(url);
          if (response.ok) {
            const data = await response.json();
            setter(data);
          }
        } catch (error) {
          console.error(`Error fetching ${url}:`, error);
        }
      }));
    } catch (error) {
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-48 bg-muted rounded" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-muted rounded" />)}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-64 bg-muted rounded" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl mb-2">Estadísticas</h1>
            <p className="text-muted-foreground">Métricas y análisis de tu web</p>
          </div>
          <button 
            onClick={fetchAllAnalytics}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Actualizar datos
          </button>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard 
            title="Visitas hoy" 
            value={overview?.views_today || 0}
            subtitle={`${overview?.unique_visitors_today || 0} únicos`}
            icon={Eye}
            color="text-blue-500"
          />
          <StatCard 
            title="Visitas semana" 
            value={overview?.views_week || 0}
            subtitle={`${overview?.unique_visitors_week || 0} únicos`}
            icon={TrendingUp}
            color="text-green-500"
          />
          <StatCard 
            title="Visitas mes" 
            value={overview?.views_month || 0}
            icon={Calendar}
            color="text-purple-500"
          />
          <StatCard 
            title="Total histórico" 
            value={overview?.total_views || 0}
            icon={BarChart3}
            color="text-orange-500"
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Daily Views Chart */}
          <div className="bg-card border border-border p-4">
            <h3 className="font-medium mb-4 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Visitas últimos 14 días
            </h3>
            <DailyChart data={viewsByDay} />
          </div>

          {/* Hourly Distribution */}
          <div className="bg-card border border-border p-4">
            <h3 className="font-medium mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Distribución por horas (última semana)
            </h3>
            <HourlyChart data={viewsByHour} />
            <div className="flex justify-between text-xs text-muted-foreground mt-2">
              <span>0:00</span>
              <span>6:00</span>
              <span>12:00</span>
              <span>18:00</span>
              <span>23:00</span>
            </div>
          </div>
        </div>

        {/* Content Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {/* Top Galleries */}
          <div className="bg-card border border-border p-4">
            <h3 className="font-medium mb-4 flex items-center gap-2">
              <Image className="w-4 h-4" />
              Galerías más vistas
            </h3>
            {topGalleries.most_visited?.length > 0 ? (
              <div className="space-y-3">
                {topGalleries.most_visited.map((gallery, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm truncate flex-1 mr-2">{gallery.title || gallery._id}</span>
                    <span className="text-xs text-muted-foreground">{gallery.views} visitas</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Sin datos todavía</p>
            )}
            
            {topGalleries.best_rated?.length > 0 && (
              <>
                <h4 className="text-sm font-medium mt-4 mb-2 flex items-center gap-1">
                  <Star className="w-3 h-3 text-yellow-500" /> Mejor valoradas
                </h4>
                <div className="space-y-2">
                  {topGalleries.best_rated.map((gallery, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-sm truncate flex-1 mr-2">{gallery.title}</span>
                      <span className="text-xs flex items-center gap-1">
                        <Star className="w-3 h-3 fill-yellow-500 text-yellow-500" />
                        {gallery.average_rating}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Top Blog Posts */}
          <div className="bg-card border border-border p-4">
            <h3 className="font-medium mb-4 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Entradas más leídas
            </h3>
            {topPosts.most_visited?.length > 0 ? (
              <div className="space-y-3">
                {topPosts.most_visited.map((post, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm truncate flex-1 mr-2">{post.title || post._id}</span>
                    <span className="text-xs text-muted-foreground">{post.views} visitas</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Sin datos todavía</p>
            )}
            
            {topPosts.best_rated?.length > 0 && (
              <>
                <h4 className="text-sm font-medium mt-4 mb-2 flex items-center gap-1">
                  <Star className="w-3 h-3 text-yellow-500" /> Mejor valoradas
                </h4>
                <div className="space-y-2">
                  {topPosts.best_rated.map((post, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-sm truncate flex-1 mr-2">{post.title}</span>
                      <span className="text-xs flex items-center gap-1">
                        <Star className="w-3 h-3 fill-yellow-500 text-yellow-500" />
                        {post.average_rating}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Top Pages */}
          <div className="bg-card border border-border p-4">
            <h3 className="font-medium mb-4 flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Páginas más visitadas
            </h3>
            {topPages.length > 0 ? (
              <div className="space-y-3">
                {topPages.slice(0, 5).map((page, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm truncate flex-1 mr-2">
                      {page.page_title || page.page_type}
                    </span>
                    <span className="text-xs text-muted-foreground">{page.views}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Sin datos todavía</p>
            )}
          </div>
        </div>

        {/* Traffic Sources and Devices */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Traffic Sources */}
          <div className="bg-card border border-border p-4">
            <h3 className="font-medium mb-4 flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Fuentes de tráfico
            </h3>
            {referrers.length > 0 ? (
              <SimpleBarChart 
                data={referrers.slice(0, 6)} 
                labelKey="source" 
                valueKey="visits"
                color="bg-green-500"
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                Sin datos de referrers todavía. Las visitas directas no se muestran aquí.
              </p>
            )}
          </div>

          {/* Device Stats */}
          <div className="bg-card border border-border p-4">
            <h3 className="font-medium mb-4 flex items-center gap-2">
              <Monitor className="w-4 h-4" />
              Dispositivos
            </h3>
            <DeviceStats devices={deviceStats.devices} />
            
            {deviceStats.browsers?.length > 0 && (
              <>
                <h4 className="text-sm font-medium mt-6 mb-3">Navegadores</h4>
                <SimpleBarChart 
                  data={deviceStats.browsers.slice(0, 5)} 
                  labelKey="browser" 
                  valueKey="count"
                  color="bg-purple-500"
                />
              </>
            )}
          </div>
        </div>

        {/* Geographic Distribution */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Origen geográfico de las visitas
            </h3>
            <div className="flex gap-3 text-xs text-muted-foreground">
              <span>24h: <strong className="text-foreground">{visitorStats.total_24h}</strong></span>
              <span>7d: <strong className="text-foreground">{visitorStats.total_7d}</strong></span>
              <span>30d: <strong className="text-foreground">{visitorStats.total_30d}</strong></span>
            </div>
          </div>

          {visitorStats.by_city?.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Map */}
              <div className="lg:col-span-2 bg-card border border-border p-2" style={{ height: '420px' }} data-testid="visitor-map">
                <MapContainer
                  center={[20, 0]}
                  zoom={2}
                  scrollWheelZoom={false}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {visitorStats.by_city
                    .filter(c => c.lat && c.lon)
                    .map((city, i) => {
                      const radius = Math.min(6 + Math.log2(city.visits + 1) * 3, 28);
                      return (
                        <CircleMarker
                          key={i}
                          center={[city.lat, city.lon]}
                          radius={radius}
                          pathOptions={{ fillColor: '#22c55e', color: '#16a34a', fillOpacity: 0.55, weight: 1.5 }}
                        >
                          <Popup>
                            <div className="text-xs">
                              <p className="font-bold">{city.city || 'Desconocida'}</p>
                              <p className="text-muted-foreground">{city.country}</p>
                              <p>{city.visits} visitas</p>
                            </div>
                          </Popup>
                        </CircleMarker>
                      );
                    })}
                </MapContainer>
              </div>

              {/* Lists */}
              <div className="space-y-4">
                <div className="bg-card border border-border p-4">
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Globe className="w-3.5 h-3.5" /> Top países
                  </h4>
                  {visitorStats.by_country.slice(0, 8).length > 0 ? (
                    <div className="space-y-2" data-testid="top-countries-list">
                      {visitorStats.by_country.slice(0, 8).map((c, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span className="truncate flex-1 mr-2">
                            {c.countryCode && <span className="mr-1 text-xs text-muted-foreground">{c.countryCode}</span>}
                            {c.country || 'Desconocido'}
                          </span>
                          <span className="text-xs text-muted-foreground">{c.visits} ({c.unique_visitors} únicos)</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">Sin datos</p>
                  )}
                </div>

                <div className="bg-card border border-border p-4">
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5" /> Top ciudades
                  </h4>
                  {visitorStats.by_city.slice(0, 8).length > 0 ? (
                    <div className="space-y-2">
                      {visitorStats.by_city.slice(0, 8).map((c, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span className="truncate flex-1 mr-2">{c.city}, <span className="text-muted-foreground text-xs">{c.country}</span></span>
                          <span className="text-xs text-muted-foreground">{c.visits}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">Sin datos</p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-card border border-border p-8 text-center text-sm text-muted-foreground">
              <MapPin className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p>Aún no hay datos geográficos.</p>
              <p className="text-xs mt-1">Los datos se acumulan a partir de las visitas reales (~10% de muestreo).</p>
            </div>
          )}
        </div>

        {/* Info Note */}
        <div className="mt-8 p-4 bg-muted/50 border border-border text-sm text-muted-foreground">
          <p>
            <strong>Nota:</strong> Las estadísticas se actualizan en tiempo real.
            La geolocalización es aproximada (a nivel de ciudad/país) y se obtiene desde la IP pública mediante ip-api.com.
            No se almacenan datos personales identificables.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminAnalytics;
